import { makeStyles } from '@material-ui/core/styles';
import typography from '@/theme/typography';
import { TextColors, TextOpacities, SecondaryColors, SurfaceColors } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const getDarkTheme = (muiTheme: any) => ({
  root: {
    '& $header, $description, $footer': {
      color: hexToRGBA(TextColors.light, TextOpacities.light2),
    },

    '& $title': {
      color: TextColors.light,
    },

    '& $category': {
      color: SecondaryColors.main,

      '&:hover': {
        color: SecondaryColors.light,
      },
    },

    '&$rootSkeleton': {
      '& .skeleton': {
        background: hexToRGBA(SurfaceColors.light, 0.5),
      },
      '&.animated': {
        '& .skeleton': {
          animation: `$skeletonCard 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
        },
      },
    },
  },
});

const getLightTheme = (muiTheme: any) => ({
  root: {
    '& $header, $description, $footer': {
      color: hexToRGBA(TextColors.dark, TextOpacities.dark2),
    },

    '& $title': {
      color: TextColors.dark,
    },

    '& $category': {
      color: SecondaryColors.dark,

      '&:hover': {
        color: SecondaryColors.main,
      },
    },

    '&$rootSkeleton': {
      '& .skeleton': {
        background: hexToRGBA(SurfaceColors.dark, 0.5),
      },
      '&.animated': {
        '& .skeleton': {
          animation: `$skeletonCardDark 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
        },
      },
    },
  },
});

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.dark-theme': getDarkTheme(muiTheme).root,
      '&.light-theme': getLightTheme(muiTheme).root,
      padding: muiTheme.spacing(4),
      width: '100%',
      position: 'relative',

      '&:not(:hover)': {
        boxShadow: 'none',
      },
    },
    link: {
      '&.MuiButtonBase-root': {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: 1,
      },
    },
    chip: {
      zIndex: 2,
    },
    cardContent: {
      display: 'flex',

      [muiTheme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    button: {
      textAlign: 'left',
      width: '100%',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',

      '& header': {
        marginBottom: muiTheme.spacing(1),
      },
    },
    header: {
      alignItems: 'baseline',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',

      '& > *': {
        marginBottom: muiTheme.spacing(1),
        flexShrink: 0,
      },
    },
    skeleton: {
      height: '11px',
      width: '100%',

      '&:before': {
        content: '""',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        display: 'block',
      },

      '&.is-image': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        borderRadius: '4px',
      },
    },
    title: {
      '& + *': {
        marginTop: muiTheme.spacing(0.5),
      },

      '& .skeleton': {
        marginTop: '9px',
        marginBottom: '16px',
        height: '16px',
        width: '75%',

        [muiTheme.breakpoints.up('md')]: {
          width: '30%',
        },
      },
    },
    main: {
      '& + *': {
        marginTop: muiTheme.spacing(2),
      },
    },
    description: {
      ...typography.body1,

      '& .skeleton': {
        '& + *': {
          marginTop: '14px',
        },

        '&:last-child': {
          width: '50%',
        },
      },
    },
    image: {
      position: 'relative',
      marginBottom: '14px',

      [muiTheme.breakpoints.up('md')]: {
        width: '230px',
        marginRight: '28px',
        marginBottom: 'initial',
      },
    },
    spacer: {
      flex: 1,
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    category: {
      marginRight: muiTheme.spacing(1),
      marginBottom: muiTheme.spacing(1),
      textDecoration: 'none',

      '& .skeleton': {
        height: '10px',
        width: '50px',
      },
    },
    tagsWrapper: {
      display: 'flex',
      marginBottom: muiTheme.spacing(-1),

      '& .title.MuiTypography-root': {
        lineHeight: '2em',
      },
    },
    tags: {
      marginLeft: muiTheme.spacing(0.5),
    },
    chips: {
      ...typography.caption,
      margin: muiTheme.spacing(0, 0.5, 1),
      padding: muiTheme.spacing(1),
    },
    date: {
      lineHeight: '1.8em',
      flexGrow: 1,
      textAlign: 'right',
    },
    rootSkeleton: {},
    rowSkeleton: {
      [muiTheme.breakpoints.down('sm')]: {
        padding: `${muiTheme.spacing(4)}px !important`,

        '& $image': {
          width: `calc(100% + ${muiTheme.spacing(8)}px)`,
          paddingTop: `calc(56.25% + ${muiTheme.spacing(4)}px)`,
          marginLeft: muiTheme.spacing(-4),
          marginTop: muiTheme.spacing(-4),
          marginBottom: muiTheme.spacing(4),

          '& .skeleton': {
            borderRadius: 0,
          },
        },
      },
      [muiTheme.breakpoints.up('md')]: {
        padding: `${muiTheme.spacing(4)}px !important`,

        '& $image': {
          '& .skeleton': {
            width: '100%',
          },
        },

        '& > *': {
          display: 'flex',
        },
      },
    },
    gridSkeleton: {
      [muiTheme.breakpoints.down('sm')]: {
        padding: `${muiTheme.spacing(4)}px !important`,

        '& $image': {
          width: `calc(100% + ${muiTheme.spacing(8)}px)`,
          paddingTop: `calc(56.25% + ${muiTheme.spacing(4)}px)`,
          marginLeft: muiTheme.spacing(-4),
          marginTop: muiTheme.spacing(-4),
          marginBottom: muiTheme.spacing(4),
        },
      },

      [muiTheme.breakpoints.up('md')]: {
        padding: `${muiTheme.spacing(6)}px !important`,

        '& $image': {
          width: `calc(100% + ${muiTheme.spacing(12)}px)`,
          paddingTop: `calc(56.25% + ${muiTheme.spacing(6)}px)`,
          marginLeft: muiTheme.spacing(-6),
          marginTop: muiTheme.spacing(-6),
          marginBottom: muiTheme.spacing(6),
        },
      },

      '& $image': {
        '& .skeleton': {
          borderRadius: 0,
        },
      },
    },
    '@keyframes skeletonCard': {
      '0%, 100%': {
        opacity: 0.75,
      },
      '50%': {
        opacity: 1,
      },
    },
    '@keyframes skeletonCardDark': {
      '0%, 100%': {
        opacity: 0.25,
      },
      '50%': {
        opacity: 0.5,
      },
    },
  }),
  { name: 'GenFilteredListCard' },
);

export default useStyles;
